import { z } from "zod"
import { Id } from "./ids.js"

export const Provider = z.enum(["instagram", "messenger"])
export type Provider = z.infer<typeof Provider>

export const PROVIDER_LABELS: Record<Provider, string> = { instagram: "Instagram", messenger: "Messenger" }

export const ChannelStatus = z.enum(["connected", "needs_attention", "disconnected"])
export type ChannelStatus = z.infer<typeof ChannelStatus>

export const CHANNEL_STATUS_LABELS: Record<ChannelStatus, string> = { connected: "Connected", needs_attention: "Needs attention", disconnected: "Disconnected" }

/** The four capabilities the product reasons about. Each maps to provider permissions in the adapter. */
export const Capability = z.enum(["receive_messages", "send_messages", "read_comments", "private_replies"])
export type Capability = z.infer<typeof Capability>

export const CAPABILITY_LABELS: Record<Capability, string> = {
  receive_messages: "Receive messages",
  send_messages: "Send messages",
  read_comments: "Read and reply to comments",
  private_replies: "Send supported private replies",
}

export const CapabilityState = z.enum(["available", "missing", "paused"])
export type CapabilityState = z.infer<typeof CapabilityState>

export const CapabilitySnapshot = z.record(Capability, CapabilityState)
export type CapabilitySnapshot = z.infer<typeof CapabilitySnapshot>

export const ChannelConnection = z.object({
  id: Id,
  provider: Provider,
  externalAccountId: z.string(),
  displayName: z.string(),
  handle: z.string().nullable(),
  status: ChannelStatus,
  capabilities: CapabilitySnapshot,
  issueSummary: z.string().nullable(),
  connectedByName: z.string(),
  connectedAt: z.iso.datetime(),
  lastCheckedAt: z.iso.datetime().nullable(),
  lastSyncedAt: z.iso.datetime().nullable(),
  issueDetectedAt: z.iso.datetime().nullable(),
  apiVersion: z.string(),
})
export type ChannelConnection = z.infer<typeof ChannelConnection>

export const ChannelConnectionDetail = ChannelConnection.extend({
  grantedScopes: z.array(z.string()),
  /** Work that depends on missing capabilities; counts come from authoritative tables. */
  affected: z.object({ pausedAutomations: z.number().int(), pausedModerationRules: z.number().int() }),
})
export type ChannelConnectionDetail = z.infer<typeof ChannelConnectionDetail>

export const ChannelsResponse = z.object({ connections: z.array(ChannelConnection), sandbox: z.boolean() })
export type ChannelsResponse = z.infer<typeof ChannelsResponse>

export const CandidateAccount = z.object({
  externalAccountId: z.string(),
  displayName: z.string(),
  handle: z.string().nullable(),
  kind: z.enum(["instagram_business", "facebook_page"]),
  capabilities: CapabilitySnapshot,
  ready: z.boolean(),
  alreadyConnected: z.boolean(),
})
export type CandidateAccount = z.infer<typeof CandidateAccount>

export const ConnectionAttemptStatus = z.enum(["pending", "authorized", "completed", "failed", "expired"])
export type ConnectionAttemptStatus = z.infer<typeof ConnectionAttemptStatus>

export const ConnectionAttempt = z.object({
  id: Id,
  provider: Provider,
  status: ConnectionAttemptStatus,
  accounts: z.array(CandidateAccount),
  grantedScopes: z.array(z.string()),
  connectedConnectionIds: z.array(Id),
  error: z.string().nullable(),
  expiresAt: z.iso.datetime(),
})
export type ConnectionAttempt = z.infer<typeof ConnectionAttempt>

export const StartConnectionInput = z.object({ provider: Provider })
export const StartConnectionResult = z.object({ attemptId: Id, authorizationUrl: z.url(), sandbox: z.boolean() })
export type StartConnectionResult = z.infer<typeof StartConnectionResult>

export const ConnectAccountsInput = z.object({ externalAccountIds: z.array(z.string().min(1)).min(1).max(20) })
export type ConnectAccountsInput = z.infer<typeof ConnectAccountsInput>
